import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ListView } from '@progress/kendo-react-listview';
import type { ListViewItemProps } from '@progress/kendo-react-listview';
import { toZonedTime } from 'date-fns-tz';
import { useSchedule } from '../context/ScheduleContext';
import { resolveTimezone } from '../utils/dateUtils';
import type { SchedulingQueueConfig, ScheduleEvent, Resource, AiChatPanelConfig } from '../types';
import { AiChatPanel } from './AiChatPanel';
import { WaitlistPanel } from '../waitlist/WaitlistPanel';
import { GroupRosterPanel } from '../group/GroupRosterPanel';

const PANEL_MIN_WIDTH = 200;
const PANEL_MAX_WIDTH = 600;
const DRAG_MIME = 'text/plain';

// ─── Collapsed strip ────────────────────────────────────────────────────────

function CollapsedStrip({ label, onExpand }: { label: string; onExpand: () => void }) {
  return (
    <div
      style={{
        width: 28,
        flexShrink: 0,
        borderLeft: '1px solid #e2e8f0',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 10,
        userSelect: 'none',
      }}
    >
      <button
        onClick={onExpand}
        title={`Expand ${label} panel`}
        style={{
          background: 'none',
          border: '1px solid #e2e8f0',
          borderRadius: 4,
          cursor: 'pointer',
          padding: '3px 4px',
          fontSize: 11,
          color: '#64748b',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
        }}
      >
        {label} ›
      </button>
    </div>
  );
}

// ─── HTML helpers ──────────────────────────────────────────────────────────

const HTML_TAG_RE = /<[a-zA-Z/]/;

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '');
}

function RenderHtml({ text, style }: { text: string; style?: React.CSSProperties }) {
  if (!HTML_TAG_RE.test(text)) return <span style={style}>{text}</span>;
  // eslint-disable-next-line react/no-danger
  return <span style={style} dangerouslySetInnerHTML={{ __html: text }} />;
}

// ─── Wait list helpers ─────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function DragHandle() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0, color: '#94a3b8' }}
    >
      <circle cx="9" cy="5" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="5" r="1" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="9" cy="19" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="19" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function formatEventTime(startUtc: string, endUtc: string, timezone: string): string {
  if (!startUtc) return 'Unscheduled';
  const start = toZonedTime(new Date(startUtc), timezone);
  const fmt = (d: Date) =>
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: timezone });
  const datePart = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: timezone });
  if (!endUtc) return `${datePart} ${fmt(start)}`;
  const end = toZonedTime(new Date(endUtc), timezone);
  return `${datePart}  ${fmt(start)} – ${fmt(end)}`;
}

function formatDuration(startUtc: string, endUtc: string): string {
  if (!startUtc || !endUtc) return '';
  const ms = new Date(endUtc).getTime() - new Date(startUtc).getTime();
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

interface QueueItemProps extends ListViewItemProps {
  resources: Resource[] | undefined;
  timezone: string;
  onSelect: (event: ScheduleEvent) => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
}

function QueueItemCard({ dataItem, resources, timezone, onSelect, onDragStart, onDragEnd }: QueueItemProps) {
  const event = dataItem as ScheduleEvent;
  const resource = resources?.find((r) => r.id === event.resourceId);
  const dotColor = event.color ?? resource?.color ?? '#64748b';
  const timeLabel = formatEventTime(event.startUtc, event.endUtc, timezone);
  const duration = formatDuration(event.startUtc, event.endUtc);
  const isUnscheduled = !event.startUtc;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={() => onSelect(event)}
      title="Click to select · Drag to schedule"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 7,
        padding: '7px 10px 7px 8px',
        borderBottom: '1px solid #f1f5f9',
        cursor: 'grab',
        background: '#fff',
        transition: 'background 0.1s',
        userSelect: 'none',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#fff'; }}
    >
      <div style={{ paddingTop: 2, flexShrink: 0 }}><DragHandle /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {resource && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0, display: 'inline-block' }} />
            <span style={{ fontSize: 10, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {resource.name}
            </span>
          </div>
        )}
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
          {event.title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: isUnscheduled ? '#94a3b8' : '#475569', fontStyle: isUnscheduled ? 'italic' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {timeLabel}
          </span>
          {duration && (
            <span style={{ fontSize: 10, color: '#94a3b8', background: '#f1f5f9', borderRadius: 3, padding: '0 4px', flexShrink: 0 }}>
              {duration}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Wait list content ─────────────────────────────────────────────────────

function QueueContent({ panelConfig }: { panelConfig: SchedulingQueueConfig }) {
  const { config, queuedEvents, queueDragRef, callbacks, resourcePage } = useSchedule();
  const { resources, resourcesPerPage: rppConfig, view, resourceGrouping, timezone, observeDaylightSaving } = config;
  const resolvedTimezone = resolveTimezone(timezone, observeDaylightSaving);

  const [search, setSearch] = useState('');

  const resourcesPerPage = typeof rppConfig === 'number' ? rppConfig : (rppConfig?.[view] ?? 2);
  const paginationActive = resourceGrouping !== 'byResource' && (resources?.length ?? 0) > resourcesPerPage;

  const visibleResourceIds = useMemo(() => {
    if (!paginationActive || !resources) return new Set(resources?.map((r) => r.id) ?? []);
    const start = resourcePage * resourcesPerPage;
    return new Set(resources.slice(start, start + resourcesPerPage).map((r) => r.id));
  }, [paginationActive, resources, resourcePage, resourcesPerPage]);

  const filteredEvents = useMemo(() => {
    let evs = queuedEvents;
    if (panelConfig.filterByVisibleResources) {
      evs = evs.filter((e) => !e.resourceId || visibleResourceIds.has(e.resourceId));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      evs = evs.filter((e) => {
        if (e.title.toLowerCase().includes(q)) return true;
        const res = resources?.find((r) => r.id === e.resourceId);
        return res?.name.toLowerCase().includes(q) ?? false;
      });
    }
    return evs;
  }, [queuedEvents, panelConfig.filterByVisibleResources, visibleResourceIds, search, resources]);

  const handleSelect = useCallback(
    (event: ScheduleEvent) => { callbacks.onQueuedEventSelected?.(event); },
    [callbacks],
  );

  const handleDragStart = useCallback(
    (event: ScheduleEvent) => (e: React.DragEvent) => {
      queueDragRef.current = event.id;
      e.dataTransfer.setData(DRAG_MIME, event.id);
      e.dataTransfer.effectAllowed = 'move';
    },
    [queueDragRef],
  );

  const handleDragEnd = useCallback(() => { queueDragRef.current = null; }, [queueDragRef]);

  const ItemComponent = useMemo(
    () =>
      (props: ListViewItemProps) => (
        <QueueItemCard
          {...props}
          resources={resources}
          timezone={resolvedTimezone}
          onSelect={handleSelect}
          onDragStart={handleDragStart(props.dataItem as ScheduleEvent)}
          onDragEnd={handleDragEnd}
        />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resources, resolvedTimezone, handleSelect, handleDragStart, handleDragEnd],
  );

  return (
    <>
      {/* Search */}
      <div style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, position: 'relative' }}>
        <SearchIcon />
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px 4px 26px',
            fontSize: 12,
            border: '1px solid #cbd5e1',
            borderRadius: 4,
            background: '#fff',
            color: '#1e293b',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = '#3b82f6'; }}
          onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = '#cbd5e1'; }}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 13, padding: '0 2px', lineHeight: 1 }}
            title="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {/* Drag hint */}
      <div style={{ padding: '4px 10px', fontSize: 10, color: '#94a3b8', flexShrink: 0, background: '#fafafa', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 4 }}>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="5 9 2 12 5 15" /><polyline points="9 5 12 2 15 5" />
          <polyline points="15 19 12 22 9 19" /><polyline points="19 9 22 12 19 15" />
          <line x1="2" y1="12" x2="22" y2="12" /><line x1="12" y1="2" x2="12" y2="22" />
        </svg>
        Drag onto schedule to place
      </div>

      {/* Event list */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', background: '#fff' }}>
        {filteredEvents.length === 0 ? (
          <div style={{ padding: '20px 12px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
            {queuedEvents.length === 0 ? 'No queued events' : 'No matches found'}
          </div>
        ) : (
          <ListView data={filteredEvents} item={ItemComponent} style={{ height: '100%', border: 'none', overflow: 'auto' }} />
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '4px 10px', borderTop: '1px solid #e2e8f0', fontSize: 11, color: '#94a3b8', flexShrink: 0, background: '#f8fafc' }}>
        {filteredEvents.length === queuedEvents.length
          ? `${queuedEvents.length} ${queuedEvents.length === 1 ? 'event' : 'events'}`
          : `${filteredEvents.length} of ${queuedEvents.length} shown`}
      </div>
    </>
  );
}

// ─── Properties content ────────────────────────────────────────────────────

function PropertiesContent() {
  const { selectedEvent, eventProperties } = useSchedule();

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      {!selectedEvent ? (
        <div style={{ padding: '20px 12px', fontSize: 12, color: '#94a3b8', textAlign: 'center', lineHeight: 1.6 }}>
          Click an event to view its properties.
        </div>
      ) : (
        <div style={{ padding: '10px 12px' }}>
          <div
            style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 10, borderBottom: '1px solid #e2e8f0', paddingBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={stripHtml(selectedEvent.title)}
          >
            <RenderHtml text={selectedEvent.title} />
          </div>
          {eventProperties.length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '8px 0' }}>
              No properties to display.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '7px 10px', alignItems: 'baseline' }}>
              {eventProperties.map(({ label, value }, i) => (
                <React.Fragment key={i}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', paddingTop: 1 }}>
                    {label}
                  </span>
                  <RenderHtml text={value} style={{ fontSize: 12, color: '#1e293b', wordBreak: 'break-word', lineHeight: 1.4 }} />
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Combined right panel ──────────────────────────────────────────────────

type TabId = 'queue' | 'properties' | 'ai' | 'backfillQueue' | 'groupRoster';

interface TabDef {
  id: TabId;
  label: string;
  badge?: number;
}

export function RightPanel() {
  const { config, queuedEvents, selectedEvent, waitlistHandle, groupTherapyHandle } = useSchedule();

  const schedulingQueueConfig = config.schedulingQueue;
  const propertiesConfig = config.propertiesPanel;
  const aiChatConfig: AiChatPanelConfig | undefined = config.aiChatPanel;
  const backfillEnabled = waitlistHandle.enabled;
  const groupEnabled = groupTherapyHandle.enabled;

  // Count open group sessions for badge
  const openGroupCount = useMemo(() => {
    if (!groupEnabled) return 0;
    let count = 0;
    for (const session of groupTherapyHandle.sessionsMap.values()) {
      if (session.isOpen && session.enrolledCount < session.capacity) count++;
    }
    return count;
  }, [groupEnabled, groupTherapyHandle.sessionsMap]);

  // Build ordered tab list from whichever panels are configured
  const tabs = useMemo<TabDef[]>(() => {
    const result: TabDef[] = [];
    if (schedulingQueueConfig) result.push({ id: 'queue', label: schedulingQueueConfig.label ?? 'Queue', badge: queuedEvents.length > 0 ? queuedEvents.length : undefined });
    if (propertiesConfig) result.push({ id: 'properties', label: propertiesConfig.label ?? 'Properties' });
    if (aiChatConfig) result.push({ id: 'ai', label: aiChatConfig.label ?? 'AI Assistant' });
    if (backfillEnabled) result.push({ id: 'backfillQueue', label: 'Waitlist', badge: waitlistHandle.summary?.urgentCount ?? undefined });
    if (groupEnabled) result.push({ id: 'groupRoster', label: 'Groups', badge: openGroupCount > 0 ? openGroupCount : undefined });
    return result;
  }, [schedulingQueueConfig, propertiesConfig, aiChatConfig, queuedEvents.length, backfillEnabled, waitlistHandle.summary?.urgentCount, groupEnabled, openGroupCount]);

  const defaultTabId = tabs[0]?.id ?? 'queue';
  const [activeTab, setActiveTab] = useState<TabId>(defaultTabId);

  // Auto-switch to Properties tab when an event is clicked
  useEffect(() => {
    if (selectedEvent && propertiesConfig) {
      setActiveTab('properties');
    }
  }, [selectedEvent, propertiesConfig]);

  // Auto-switch to Groups tab when a group session event is clicked
  useEffect(() => {
    if (selectedEvent && groupEnabled && groupTherapyHandle.sessionsMap.has(selectedEvent.id)) {
      setActiveTab('groupRoster');
    }
  }, [selectedEvent, groupEnabled, groupTherapyHandle.sessionsMap]);

  const firstConfig = schedulingQueueConfig ?? propertiesConfig ?? aiChatConfig;
  const defaultWidth = aiChatConfig && !schedulingQueueConfig && !propertiesConfig && !backfillEnabled
    ? (aiChatConfig.defaultWidth ?? 360)
    : (schedulingQueueConfig?.defaultWidth ?? propertiesConfig?.defaultWidth ?? 280);

  const [collapsed, setCollapsed] = useState(firstConfig?.defaultCollapsed ?? false);
  // Auto-switch to backfillQueue tab when it receives an urgent notification
  useEffect(() => {
    if (backfillEnabled && waitlistHandle.notification && activeTab !== 'backfillQueue') {
      // Don't force-switch — let the user click "View waitlist" in the toast.
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitlistHandle.notification]);
  const [width, setWidth] = useState(defaultWidth);

  const collapseLabel = tabs.map((t) => t.label).join(' / ') || 'Panel';

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      const startX = e.clientX;
      const startW = width;
      const onMove = (ev: MouseEvent) => {
        setWidth(Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, startW + startX - ev.clientX)));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      e.preventDefault();
    },
    [width],
  );

  if (collapsed) {
    return <CollapsedStrip label={collapseLabel} onExpand={() => setCollapsed(false)} />;
  }

  const collapseBtn = (
    <button
      onClick={() => setCollapsed(true)}
      title="Collapse panel"
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 5px', fontSize: 15, color: '#94a3b8', lineHeight: 1, borderRadius: 3 }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#475569'; (e.currentTarget as HTMLButtonElement).style.background = '#e2e8f0'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#94a3b8'; (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
    >
      ›
    </button>
  );

  return (
    <div
      style={{
        width,
        flexShrink: 0,
        borderLeft: '1px solid #e2e8f0',
        background: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* ── Header / Tab bar ── */}
      {tabs.length > 1 ? (
        <div style={{ background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', flexShrink: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flex: 1, minWidth: 0, overflow: 'hidden' }}>
            {tabs.map((tab) => (
              <TabButton
                key={tab.id}
                label={tab.label}
                active={activeTab === tab.id}
                badge={tab.badge}
                onClick={() => setActiveTab(tab.id)}
              />
            ))}
          </div>
          <div style={{ padding: '0 4px', display: 'flex', alignItems: 'center' }}>
            {collapseBtn}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px 6px 10px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, background: '#f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', userSelect: 'none', letterSpacing: '0.01em' }}>
              {tabs[0]?.label ?? 'Panel'}
            </span>
            {tabs[0]?.badge !== undefined && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#3b82f6', borderRadius: 10, padding: '1px 6px', lineHeight: 1.5 }}>
                {tabs[0].badge}
              </span>
            )}
          </div>
          {collapseBtn}
        </div>
      )}

      {/* ── Content ── */}
      {/* All tab panels stay mounted to preserve their internal state (e.g. AI chat history).
          Inactive tabs are hidden via display:none rather than unmounted. */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {schedulingQueueConfig && (
          <div style={{ display: activeTab === 'queue' ? 'contents' : 'none' }}>
            <QueueContent panelConfig={schedulingQueueConfig} />
          </div>
        )}
        {propertiesConfig && (
          <div style={{ display: activeTab === 'properties' ? 'contents' : 'none' }}>
            <PropertiesContent />
          </div>
        )}
        {aiChatConfig && (
          <div style={{ display: activeTab === 'ai' ? 'contents' : 'none' }}>
            <AiChatPanel />
          </div>
        )}
        {backfillEnabled && (
          <div style={{ display: activeTab === 'backfillQueue' ? 'contents' : 'none' }}>
            <WaitlistPanel />
          </div>
        )}
        {groupEnabled && (
          <div style={{ display: activeTab === 'groupRoster' ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <GroupRosterPanel />
          </div>
        )}
      </div>

      {/* ── Resize handle (left edge) ── */}
      <div
        onMouseDown={handleResizeStart}
        style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', cursor: 'col-resize', zIndex: 10, transition: 'background 0.15s' }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(59,130,246,0.35)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
      />
    </div>
  );
}

// ─── Tab button ────────────────────────────────────────────────────────────

function TabButton({ label, active, badge, onClick }: { label: string; active: boolean; badge?: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '7px 12px',
        fontSize: 12,
        fontWeight: active ? 700 : 400,
        color: active ? '#2563eb' : '#64748b',
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        transition: 'color 0.15s, border-color 0.15s',
      }}
      onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.color = '#475569'; }}
      onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.color = '#64748b'; }}
    >
      {label}
      {badge !== undefined && (
        <span style={{ fontSize: 10, fontWeight: 700, color: active ? '#fff' : '#fff', background: active ? '#3b82f6' : '#94a3b8', borderRadius: 10, padding: '1px 5px', lineHeight: 1.5 }}>
          {badge}
        </span>
      )}
    </button>
  );
}
